import React, { useState, useRef, useEffect } from 'react';
import './ImageCropper.css';

const ImageCropper = ({
  image,
  onCrop,
  onCancel,
  aspectRatio = null,
  minWidth = 200,
  minHeight = 100,
  maxWidth = 1400,
  maxHeight = 600,
  maxCropWidth = 3000,  // Maximum crop rectangle width
  maxCropHeight = 3000, // Maximum crop rectangle height
  initialDimensions = null,
  showSearchDisplayArea = true
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [showDimensions] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [manualWidth, setManualWidth] = useState('');
  const [manualHeight, setManualHeight] = useState('');
  const [manualRatio, setManualRatio] = useState('');

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Utility function to clamp crop rectangle within viewport bounds
  const clampCropToBounds = (cropRect, viewportWidth, viewportHeight) => {
    let { x, y, width, height } = cropRect;

    width = Math.max(minWidth, width);
    height = Math.max(minHeight, height);

    if (x < 0) {
      width = width + x;
      x = 0;
    }
    if (y < 0) {
      height = height + y;
      y = 0;
    }
    if (x + width > viewportWidth) {
      width = viewportWidth - x;
    }
    if (y + height > viewportHeight) {
      height = viewportHeight - y;
    }

    width = Math.max(minWidth, width);
    height = Math.max(minHeight, height);

    return { x, y, width, height };
  };

  useEffect(() => {
    if (!image || !imageRef.current) return;

    const img = imageRef.current;
    let cancelled = false;

    const handleLoad = () => {
      if (cancelled || !containerRef.current) return;
      clearTimeout(loadTimeout);

      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      const imgAspectRatio = img.naturalWidth / img.naturalHeight;
      let displayWidth, displayHeight;

      if (imgAspectRatio > containerWidth / containerHeight) {
        displayWidth = containerWidth;
        displayHeight = containerWidth / imgAspectRatio;
      } else {
        displayHeight = containerHeight;
        displayWidth = containerHeight * imgAspectRatio;
      }

      setImageSize({ width: displayWidth, height: displayHeight });

      const imageOffsetX = (containerWidth - displayWidth) / 2;
      const imageOffsetY = (containerHeight - displayHeight) / 2;

      let cropWidth, cropHeight, cropX, cropY;

      if (initialDimensions) {
        let initialWidth = initialDimensions.displayWidth || initialDimensions.width;
        let initialHeight = initialDimensions.displayHeight || initialDimensions.height;

        if (typeof initialWidth === 'string' && initialWidth.includes('%')) {
          initialWidth = displayWidth * (parseFloat(initialWidth) / 100);
        }
        if (typeof initialHeight === 'string' && initialHeight.includes('%')) {
          initialHeight = displayHeight * (parseFloat(initialHeight) / 100);
        }

        cropWidth = Math.max(minWidth, Math.min(initialWidth || displayWidth * 0.8, displayWidth));
        cropHeight = Math.max(minHeight, Math.min(initialHeight || displayHeight * 0.8, displayHeight));

        if (initialDimensions.cropX !== undefined && initialDimensions.cropY !== undefined) {
          cropX = initialDimensions.cropX;
          cropY = initialDimensions.cropY;
        } else {
          cropX = imageOffsetX + (displayWidth - cropWidth) / 2;
          cropY = imageOffsetY + (displayHeight - cropHeight) / 2;
        }
      } else {
        cropWidth = displayWidth * 0.8;
        cropHeight = displayHeight * 0.8;
        cropX = imageOffsetX + (displayWidth - cropWidth) / 2;
        cropY = imageOffsetY + (displayHeight - cropHeight) / 2;
      }

      const clampedCrop = clampCropToBounds({ x: cropX, y: cropY, width: cropWidth, height: cropHeight }, containerWidth, containerHeight);
      setCrop(clampedCrop);
    };

    const loadTimeout = setTimeout(() => {
      if (cancelled) return;
      if (!img.complete || img.naturalWidth === 0) {
        setImageSize({ width: 800, height: 600 });
        setCrop({ x: 100, y: 100, width: 600, height: 400 });
      }
    }, 5000);

    img.onload = handleLoad;
    img.onerror = () => clearTimeout(loadTimeout);

    // If image is already loaded (data URL or cached), onload won't fire — call handler manually.
    if (img.complete && img.naturalWidth > 0) {
      handleLoad();
    }

    return () => {
      cancelled = true;
      clearTimeout(loadTimeout);
      img.onload = null;
      img.onerror = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const isInsideCrop = x >= crop.x && x <= crop.x + crop.width &&
                         y >= crop.y && y <= crop.y + crop.height;

    const handleSize = 15;
    const handles = {
      'nw': { x: crop.x, y: crop.y },
      'ne': { x: crop.x + crop.width, y: crop.y },
      'sw': { x: crop.x, y: crop.y + crop.height },
      'se': { x: crop.x + crop.width, y: crop.y + crop.height }
    };

    let isOnHandle = false;
    for (const [handle, pos] of Object.entries(handles)) {
      if (Math.abs(x - pos.x) <= handleSize && Math.abs(y - pos.y) <= handleSize) {
        setIsResizing(true);
        setResizeHandle(handle);
        setDragStart({ x, y });
        isOnHandle = true;
        break;
      }
    }

    if (!isOnHandle && isInsideCrop) {
      setIsDragging(true);
      setDragStart({ x: x - crop.x, y: y - crop.y });
    } else if (!isOnHandle) {
      setIsDrawing(true);
      setDrawStart({ x, y });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing && !isDrawing) return;

    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isDragging) {
      const currentImageWidth = containerRef.current.clientWidth;
      const currentImageHeight = containerRef.current.clientHeight;
      const newX = Math.max(0, Math.min(x - dragStart.x, currentImageWidth - crop.width));
      const newY = Math.max(0, Math.min(y - dragStart.y, currentImageHeight - crop.height));
      setCrop(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      let newCrop = { ...crop };

      switch (resizeHandle) {
        case 'se':
          newCrop.width = Math.min(Math.max(minWidth, x - crop.x), maxCropWidth);
          newCrop.height = Math.min(Math.max(minHeight, y - crop.y), maxCropHeight);
          break;
        case 'sw': {
          const newWidth2 = Math.min(Math.max(minWidth, crop.x + crop.width - x), maxCropWidth);
          newCrop.height = Math.min(Math.max(minHeight, y - crop.y), maxCropHeight);
          newCrop.x = crop.x + crop.width - newWidth2;
          newCrop.width = newWidth2;
          break;
        }
        case 'ne': {
          newCrop.width = Math.min(Math.max(minWidth, x - crop.x), maxCropWidth);
          const newHeight3 = Math.min(Math.max(minHeight, crop.y + crop.height - y), maxCropHeight);
          newCrop.y = crop.y + crop.height - newHeight3;
          newCrop.height = newHeight3;
          break;
        }
        case 'nw': {
          const newWidth4 = Math.min(Math.max(minWidth, crop.x + crop.width - x), maxCropWidth);
          const newHeight4 = Math.min(Math.max(minHeight, crop.y + crop.height - y), maxCropHeight);
          newCrop.x = crop.x + crop.width - newWidth4;
          newCrop.y = crop.y + crop.height - newHeight4;
          newCrop.width = newWidth4;
          newCrop.height = newHeight4;
          break;
        }
        default:
          break;
      }

      const currentImageWidth = containerRef.current.clientWidth;
      const currentImageHeight = containerRef.current.clientHeight;

      if (resizeHandle === 'se') {
        if (newCrop.x + newCrop.width > currentImageWidth) newCrop.width = currentImageWidth - newCrop.x;
        if (newCrop.y + newCrop.height > currentImageHeight) newCrop.height = currentImageHeight - newCrop.y;
      } else if (resizeHandle === 'sw') {
        if (newCrop.x < 0) { newCrop.width += newCrop.x; newCrop.x = 0; }
        if (newCrop.y + newCrop.height > currentImageHeight) newCrop.height = currentImageHeight - newCrop.y;
      } else if (resizeHandle === 'ne') {
        if (newCrop.x + newCrop.width > currentImageWidth) newCrop.width = currentImageWidth - newCrop.x;
        if (newCrop.y < 0) { newCrop.height += newCrop.y; newCrop.y = 0; }
      } else if (resizeHandle === 'nw') {
        if (newCrop.x < 0) { newCrop.width += newCrop.x; newCrop.x = 0; }
        if (newCrop.y < 0) { newCrop.height += newCrop.y; newCrop.y = 0; }
      }

      if (newCrop.width < minWidth) newCrop.width = minWidth;
      if (newCrop.height < minHeight) newCrop.height = minHeight;

      setCrop(newCrop);
    } else if (isDrawing) {
      // Follow mouse exactly during drawing — no minimum size enforced here to prevent jumping.
      // Minimum size will be applied on mouse up.
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      const clampedX = Math.max(0, Math.min(x, containerWidth));
      const clampedY = Math.max(0, Math.min(y, containerHeight));

      const newX = Math.min(drawStart.x, clampedX);
      const newY = Math.min(drawStart.y, clampedY);
      const newWidth = Math.abs(clampedX - drawStart.x);
      const newHeight = Math.abs(clampedY - drawStart.y);

      setCrop({
        x: newX,
        y: newY,
        width: Math.max(1, newWidth),
        height: Math.max(1, newHeight)
      });
    }
  };

  const handleMouseUp = () => {
    if (isDrawing) {
      // Apply minimum size constraints now that the user has finished drawing.
      // We do this here (not during mousemove) to prevent the rectangle from
      // jumping while the user is still dragging small distances.
      setCrop(prev => {
        if (prev.width < minWidth || prev.height < minHeight) {
          const containerWidth = containerRef.current?.clientWidth || imageSize.width;
          const containerHeight = containerRef.current?.clientHeight || imageSize.height;
          const finalWidth = Math.max(minWidth, prev.width);
          const finalHeight = Math.max(minHeight, prev.height);
          const clampedX = Math.max(0, Math.min(prev.x, containerWidth - finalWidth));
          const clampedY = Math.max(0, Math.min(prev.y, containerHeight - finalHeight));
          return { x: clampedX, y: clampedY, width: finalWidth, height: finalHeight };
        }
        return prev;
      });
    }

    setIsDragging(false);
    setIsResizing(false);
    setIsDrawing(false);
    setResizeHandle(null);
  };

  const handleCrop = () => {
    if (!imageRef.current) return;
    if (!imageRef.current.complete) return;
    if (imageRef.current.naturalWidth === 0 || imageRef.current.naturalHeight === 0) return;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;

      const currentImageWidth = imageSize.width > 0 ? imageSize.width : naturalWidth;
      const currentImageHeight = imageSize.height > 0 ? imageSize.height : naturalHeight;
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      if (currentImageWidth <= 0 || currentImageHeight <= 0) return;

      const scaleX = naturalWidth / currentImageWidth;
      const scaleY = naturalHeight / currentImageHeight;

      const imageOffsetX = (containerWidth - currentImageWidth) / 2;
      const imageOffsetY = (containerHeight - currentImageHeight) / 2;

      const relativeCropX = Math.max(0, crop.x - imageOffsetX);
      const relativeCropY = Math.max(0, crop.y - imageOffsetY);
      const relativeCropWidth = Math.min(crop.width, currentImageWidth - relativeCropX);
      const relativeCropHeight = Math.min(crop.height, currentImageHeight - relativeCropY);

      const cropX = relativeCropX * scaleX;
      const cropY = relativeCropY * scaleY;
      const cropWidth = relativeCropWidth * scaleX;
      const cropHeight = relativeCropHeight * scaleY;

      const maxCanvasDimension = 5000;
      const limitedCropWidth = Math.min(cropWidth, maxCanvasDimension);
      const limitedCropHeight = Math.min(cropHeight, maxCanvasDimension);

      // Scale output to max 1200px wide for fast loading on desktop and mobile.
      const maxOutputWidth = 1200;
      let outputWidth = limitedCropWidth;
      let outputHeight = limitedCropHeight;
      if (outputWidth > maxOutputWidth) {
        const scale = maxOutputWidth / outputWidth;
        outputWidth = maxOutputWidth;
        outputHeight = Math.round(outputHeight * scale);
      }

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      imageRef.current.crossOrigin = 'anonymous';

      ctx.drawImage(
        imageRef.current,
        cropX, cropY, limitedCropWidth, limitedCropHeight,
        0, 0, outputWidth, outputHeight
      );

      const dimensions = {
        width: Math.round(outputWidth),
        height: Math.round(outputHeight),
        displayWidth: Math.round(crop.width),
        displayHeight: Math.round(crop.height),
        cropX: Math.round(crop.x),
        cropY: Math.round(crop.y)
      };

      try {
        canvas.toBlob((blob) => {
          if (blob) {
            onCrop(new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' }), dimensions);
          } else {
            fetch(canvas.toDataURL('image/jpeg', 0.8))
              .then(res => res.blob())
              .then(b => onCrop(new File([b], 'cropped-image.jpg', { type: 'image/jpeg' }), dimensions))
              .catch(() => onCrop(null, dimensions));
          }
        }, 'image/jpeg', 0.8);
      } catch (blobError) {
        fetch(canvas.toDataURL('image/jpeg', 0.8))
          .then(res => res.blob())
          .then(b => onCrop(new File([b], 'cropped-image.jpg', { type: 'image/jpeg' }), dimensions))
          .catch(() => onCrop(null, dimensions));
      }
    } catch (error) {
      const naturalWidth = imageRef.current?.naturalWidth || 1;
      const naturalHeight = imageRef.current?.naturalHeight || 1;
      const currentImageWidth = imageSize.width > 0 ? imageSize.width : naturalWidth;
      const currentImageHeight = imageSize.height > 0 ? imageSize.height : naturalHeight;
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const scaleX = naturalWidth / currentImageWidth;
      const scaleY = naturalHeight / currentImageHeight;
      const imageOffsetX = (containerWidth - currentImageWidth) / 2;
      const imageOffsetY = (containerHeight - currentImageHeight) / 2;
      const relativeCropX = Math.max(0, crop.x - imageOffsetX);
      const relativeCropY = Math.max(0, crop.y - imageOffsetY);
      const relativeCropWidth = Math.min(crop.width, currentImageWidth - relativeCropX);
      const relativeCropHeight = Math.min(crop.height, currentImageHeight - relativeCropY);

      onCrop(null, {
        width: Math.round(Math.min(relativeCropWidth * scaleX, 5000)),
        height: Math.round(Math.min(relativeCropHeight * scaleY, 5000)),
        displayWidth: Math.round(crop.width),
        displayHeight: Math.round(crop.height),
        cropX: Math.round(relativeCropX),
        cropY: Math.round(relativeCropY)
      });
    }
  };

  const calculateActualDimensions = () => {
    return {
      width: Math.round(crop.width),
      height: Math.round(crop.height)
    };
  };

  const { width: actualWidth, height: actualHeight } = calculateActualDimensions();

  useEffect(() => {
    setManualWidth(actualWidth.toString());
    setManualHeight(actualHeight.toString());
    setManualRatio(actualHeight > 0 ? (actualWidth / actualHeight).toFixed(2) : '1.00');
  }, [actualWidth, actualHeight]);

  const handleManualWidthBlur = (e) => {
    const newWidth = parseInt(e.target.value) || 0;
    if (newWidth > 0 && manualRatio) {
      const newHeight = Math.round(newWidth / parseFloat(manualRatio));
      setManualHeight(newHeight.toString());
      const currentImageWidth = imageSize.width > 0 ? imageSize.width : (imageRef.current?.naturalWidth || 0);
      const currentImageHeight = imageSize.height > 0 ? imageSize.height : (imageRef.current?.naturalHeight || 0);
      const newX = Math.max(0, (currentImageWidth - newWidth) / 2);
      const newY = Math.max(0, (currentImageHeight - newHeight) / 2);
      setCrop(clampCropToBounds({ x: newX, y: newY, width: newWidth, height: newHeight }, currentImageWidth, currentImageHeight));
    }
  };

  const handleManualHeightBlur = (e) => {
    const newHeight = parseInt(e.target.value) || 0;
    if (newHeight > 0 && manualRatio) {
      const newWidth = Math.round(newHeight * parseFloat(manualRatio));
      setManualWidth(newWidth.toString());
      const currentImageWidth = imageSize.width > 0 ? imageSize.width : (imageRef.current?.naturalWidth || 0);
      const currentImageHeight = imageSize.height > 0 ? imageSize.height : (imageRef.current?.naturalHeight || 0);
      const newX = Math.max(0, (currentImageWidth - newWidth) / 2);
      const newY = Math.max(0, (currentImageHeight - newHeight) / 2);
      setCrop(clampCropToBounds({ x: newX, y: newY, width: newWidth, height: newHeight }, currentImageWidth, currentImageHeight));
    }
  };

  const handleManualRatioBlur = (e) => {
    const newRatio = parseFloat(e.target.value) || 1;
    if (newRatio > 0 && manualWidth) {
      const newHeight = Math.round(parseInt(manualWidth) / newRatio);
      setManualHeight(newHeight.toString());
      const currentImageWidth = imageSize.width > 0 ? imageSize.width : (imageRef.current?.naturalWidth || 0);
      const currentImageHeight = imageSize.height > 0 ? imageSize.height : (imageRef.current?.naturalHeight || 0);
      const displayWidth = parseInt(manualWidth);
      const newX = Math.max(0, (currentImageWidth - displayWidth) / 2);
      const newY = Math.max(0, (currentImageHeight - newHeight) / 2);
      setCrop(clampCropToBounds({ x: newX, y: newY, width: displayWidth, height: newHeight }, currentImageWidth, currentImageHeight));
    }
  };

  const handleManualWidthChange = (e) => setManualWidth(e.target.value);
  const handleManualHeightChange = (e) => setManualHeight(e.target.value);
  const handleManualRatioChange = (e) => setManualRatio(e.target.value);

  return (
    <div className="image-cropper-overlay">
      <div className="image-cropper-container">
        <div className="image-cropper-header">
          <h3>Odreži sliku</h3>
          <div className="image-cropper-controls">
            <button onClick={handleCrop} className="crop-btn">
              Odreži
            </button>
            <button onClick={onCancel} className="cancel-btn">
              Otkaži
            </button>
          </div>
        </div>

        <div className="image-cropper-content">
          <div className="dimensions-display">
            <div className="dimension-item">
              <span>Širina (prikaz):</span>
              <div className="dimension-input-group">
                <span className="dimension-value">{actualWidth}px</span>
                <input
                  type="number"
                  value={manualWidth}
                  onChange={handleManualWidthChange}
                  onBlur={handleManualWidthBlur}
                  className="dimension-input"
                  placeholder="Širina"
                  min="1"
                  max="3000"
                />
              </div>
            </div>
            <div className="dimension-item">
              <span>Visina (prikaz):</span>
              <div className="dimension-input-group">
                <span className="dimension-value">{actualHeight}px</span>
                <input
                  type="number"
                  value={manualHeight}
                  onChange={handleManualHeightChange}
                  onBlur={handleManualHeightBlur}
                  className="dimension-input"
                  placeholder="Visina"
                  min="1"
                  max="3000"
                />
              </div>
            </div>
            <div className="dimension-item">
              <span>Odnos:</span>
              <div className="dimension-input-group">
                <span className="dimension-value">{actualHeight > 0 ? (actualWidth / actualHeight).toFixed(2) : '1.00'}:1</span>
                <input
                  type="number"
                  value={manualRatio}
                  onChange={handleManualRatioChange}
                  onBlur={handleManualRatioBlur}
                  className="dimension-input"
                  placeholder="Odnos"
                  min="0.1"
                  max="10"
                  step="0.01"
                />
              </div>
            </div>
            {(crop.width >= maxCropWidth || crop.height >= maxCropHeight) && showSearchDisplayArea && (
              <div className="dimension-warning">
                <span>⚠️ Maksimalna veličina obrezivanja</span>
              </div>
            )}
          </div>

          <div
            className={`image-cropper-viewport ${isDrawing ? 'drawing' : ''}`}
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              ref={imageRef}
              src={image}
              alt="Crop preview"
              className="crop-image"
              crossOrigin="anonymous"
            />

            <div
              className={`crop-overlay ${isDragging ? 'dragging' : ''}`}
              style={{
                left: crop.x,
                top: crop.y,
                width: crop.width,
                height: crop.height
              }}
            >
              <div className="resize-handle nw" />
              <div className="resize-handle ne" />
              <div className="resize-handle sw" />
              <div className="resize-handle se" />

              {showDimensions && (
                <>
                  <div className="dimension-label width-label">
                    {Math.round(crop.width)}px
                  </div>
                  <div className="dimension-label height-label">
                    {Math.round(crop.height)}px
                  </div>
                </>
              )}
            </div>

            {showSearchDisplayArea && (
              <div
                className="search-display-indicator"
                style={{
                  position: 'absolute',
                  left: '0',
                  top: '0',
                  width: '100%',
                  height: '100%',
                  border: '2px dashed rgba(255, 255, 255, 0.6)',
                  pointerEvents: 'none',
                  zIndex: 5
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  Search.js Display Area
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
